import { Bell, ShieldCheck, ChevronDown, Sparkles, Zap } from "lucide-react";
import { SHELL } from "@/constants/testIds";
import Ni2Icon from "@/components/system/Ni2Icon";
import AppearanceSelector from "@/components/system/AppearanceSelector";
import { useLiveMode } from "@/context/LiveModeContext";

const AVATAR_URL =
  "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=facearea&facepad=2.4&w=160&h=160&q=80";

export default function TitleBar({ ni2Open, setNi2Open, inspectorOpen, setInspectorOpen }) {
  const toggleNi2 = () => {
    setNi2Open(!ni2Open);
  };
  const toggleInspector = () => {
    setInspectorOpen(!inspectorOpen);
    if (!inspectorOpen) setNi2Open(false);
  };

  return (
    <div
      data-testid={SHELL.titleBar}
      className="relative z-30 flex h-11 items-center justify-between"
    >
      {/* Left: window controls + wordmark */}
      <div className="flex items-center gap-3 pl-5">
        <span className="h-3 w-3 rounded-full bg-[#e35d5b]/70 border border-white/10" />
        <span className="h-3 w-3 rounded-full bg-[#e3a04a]/70 border border-white/10" />
        <span className="h-3 w-3 rounded-full bg-[#5cb26a]/70 border border-white/10" />
        <div className="ml-3 flex items-center gap-1.5 text-[11px] font-medium tracking-[0.16em] text-[hsl(var(--nn-ink-muted))]">
          <ShieldCheck className="h-3 w-3 text-[hsl(var(--nn-blue))]" strokeWidth={1.6} />
          NEURONEETI
        </div>
      </div>

      {/* Right: notifications, Ni², profile */}
      <div className="flex items-center gap-2.5 pr-5">
        <LiveModeToggle />

        <button className="nn-icon-btn" aria-label="Notifications">
          <Bell className="h-3.5 w-3.5" strokeWidth={1.6} />
        </button>

        <AppearanceSelector />

        {/* Ni² — icon only */}
        <button
          data-testid={SHELL.commandBtn}
          onClick={toggleNi2}
          className="grid h-8 w-8 place-items-center rounded-lg transition-all duration-200 hover:-translate-y-[0.5px]"
          style={{
            background: ni2Open
              ? "linear-gradient(180deg, rgba(160,200,255,0.28), rgba(120,165,240,0.12))"
              : "transparent",
            border: ni2Open
              ? "1px solid rgba(160,200,255,0.42)"
              : "1px solid transparent",
          }}
          aria-label="Ni² assistant"
          title="Hey Ni²"
        >
          <Ni2Icon size={24} />
        </button>

        {/* Workspace / profile pill */}
        <button
          data-testid={SHELL.syncBadge}
          onClick={toggleInspector}
          className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2.5 transition-all duration-200"
          style={{
            background:
              inspectorOpen
                ? "linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0.05))"
                : "linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02))",
            border:
              inspectorOpen
                ? "1px solid rgba(255,255,255,0.22)"
                : "1px solid rgba(255,255,255,0.1)",
          }}
          aria-label="Workspace"
        >
          <img
            src={AVATAR_URL}
            alt="Dr. Maya Rao"
            className="h-6 w-6 rounded-full object-cover"
            style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18)" }}
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <span className="text-[11.5px] font-medium text-[hsl(var(--nn-ink))]">
            Dr. Rao
          </span>
          <ChevronDown
            className="h-3 w-3 text-[hsl(var(--nn-ink-muted))]"
            strokeWidth={1.8}
            style={{
              transform: inspectorOpen ? "rotate(180deg)" : "none",
              transition: "transform 220ms ease",
            }}
          />
        </button>
      </div>
    </div>
  );
}

/**
 * Live GPT ↔ Deterministic Demo toggle.
 * Sits at the head of the right cluster in the TitleBar so a
 * presenter can flip modes in one click on stage.
 */
function LiveModeToggle() {
  const { liveActive, liveAvailable, model, setLive } = useLiveMode();
  // The pill directly reflects the *effective* mode. Clicking toggles
  // whether the user opts OUT to demo — server availability is
  // separately displayed via the badge label.
  const badge = liveActive ? "Live" : liveAvailable ? "Demo" : "Offline";
  const tooltip = liveAvailable
    ? (liveActive
        ? `Ni² Live GPT (${model || "openai/gpt-5.6-sol"}) — click to switch to Deterministic Demo`
        : "Deterministic Demo mode (explicit opt-out). Click to return to Live GPT.")
    : "Server has no OpenAI-capable key. Live GPT is unavailable — falling back to Deterministic Demo.";
  const on = liveActive;
  return (
    <button
      type="button"
      onClick={() => setLive(!liveActive)}
      className="group inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[10.5px] font-medium transition"
      style={{
        borderColor: on ? "rgba(120,220,150,0.42)" : "rgba(255,255,255,0.10)",
        background: on
          ? "linear-gradient(180deg, rgba(120,220,150,0.20), rgba(70,180,110,0.06))"
          : "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
        color: on ? "hsl(var(--nn-ok))" : "hsl(var(--nn-ink-muted))",
      }}
      title={tooltip}
      aria-label={tooltip}
      data-testid="header-live-mode-toggle"
      data-active={on || undefined}
    >
      {on ? <Zap className="h-3 w-3" strokeWidth={2} /> : <Sparkles className="h-3 w-3" strokeWidth={2} />}
      <span className="tracking-[0.06em] uppercase">{badge}</span>
      <span
        className="ml-1 inline-flex h-3.5 w-6 items-center rounded-full p-0.5 transition"
        style={{ background: on ? "rgba(120,220,150,0.42)" : "rgba(255,255,255,0.14)" }}
      >
        <span
          className="h-2.5 w-2.5 rounded-full bg-white transition"
          style={{ transform: on ? "translateX(10px)" : "translateX(0)" }}
        />
      </span>
    </button>
  );
}
